import { useEffect, useState, useRef } from 'react';
import { Button, message } from 'antd';
import LoadMore from 'components/LoadMore';
import Empty from 'components/Empty';
import { useMemoizedFn, useVirtualList } from 'ahooks';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { batchSearch, searchType } from 'services/api/conversation.convList';
import { localTypeMapBackend, backendSearch2LocalAdapter } from 'utils/search';
import SearchPageItem, { SearchPageItemSelectionProvider } from './SearchPageItem';
import style from './SearchStyle';
import copyText from 'utils/copyText';
import { useTranslate } from '@tolgee/react';
import { isValidConversationId } from '../Chat/utils';

type SearchPageSpecialTypeProps = {
  searchReturn: Conversation.SearchReturn;
  type: string;
  searchWord: string;
  isBatchSearch?: boolean;
  time: number;
  prefetchConversationsMap: Record<string, Conversation.Conversation>;
  onPrefetchConversations: (conversationIds: string[]) => Promise<void>;
};

export default function SearchPageSpecialType({
  type,
  searchReturn,
  time,
  searchWord,
  isBatchSearch,
  prefetchConversationsMap,
  onPrefetchConversations,
}: SearchPageSpecialTypeProps) {
  const { t } = useTranslate();
  const { rows: searchRows } = searchReturn || {};
  const [list, setList] = useState(searchRows || []);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const { onlineSearch } = useFlagConfigValue();

  let offsetTmp = `${time}`;
  if (list.length > 0) {
    offsetTmp = list[list.length - 1].msgTime;
  }
  const [offset, setOffset] = useState(onlineSearch ? undefined : offsetTmp);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [vList, scrollTo] = useVirtualList(list, {
    containerTarget: containerRef,
    wrapperTarget: wrapperRef,
    overscan: 3,
    itemHeight: 93,
  });

  useEffect(() => {
    scrollTo(0);
    setList(searchRows || []);
    setOffset(undefined);
    setHasMore(true);

    // `scrollTo`必须忽略依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, searchRows]);

  const [notFoundList, setNotFoundList] = useState<string[]>([]);

  const backendSearch = useMemoizedFn(async () => {
    try {
      setLoading(true);

      const conversationIds: string[] = [];
      if (isBatchSearch) {
        const { searchRow } = await batchSearch({
          type: localTypeMapBackend[type],
          keyword: searchWord,
        });
        const rows: Conversation.SearchItemUI[] = [];
        searchRow.forEach((row) => {
          if (isValidConversationId(row.conversationId)) {
            conversationIds.push(row.conversationId);
          }
          rows.push(backendSearch2LocalAdapter(row));
        });
        setList(list.concat(rows));
        setHasMore(false);
        setNotFoundList(
          searchWord
            .split(',')
            .filter((word) =>
              searchRow.every(
                ({ tradeId, keyword }) => (type === 'track' ? keyword : tradeId) !== word,
              ),
            ),
        );
      } else {
        const { searchRow, hasNext, nextOffset } = await searchType({
          type: localTypeMapBackend[type],
          keyword: searchWord.trim(),
          pageSize: 10,
          offset,
          suTalkMode: true,
        });
        const rows: Conversation.SearchItemUI[] = [];
        searchRow.forEach((row) => {
          if (isValidConversationId(row.conversationId)) {
            conversationIds.push(row.conversationId);
          }
          rows.push(backendSearch2LocalAdapter(row));
        });
        setList(list.concat(rows));
        if (hasNext) {
          setOffset(nextOffset);
        }
        setHasMore(hasNext);
      }
      // 预获取会话信息
      await onPrefetchConversations(conversationIds);
    } catch (e) {
      message.error(t('api.searchMsg'));
      throw e;
    } finally {
      setLoading(false);
    }
  });

  if (list) {
    const content = (
      <SearchPageItemSelectionProvider prefetchConversationsMap={prefetchConversationsMap}>
        <div ref={containerRef} className={style.specialType}>
          <LoadMore
            hasMore={hasMore}
            loadingDelay={100}
            loading={loading}
            onLoadMore={() => {
              return backendSearch();
            }}
            loadRenderer={(nodes, hasMore, hasError) => {
              const { loadMore, noMore, loadError } = nodes;

              if (hasError) {
                return loadError;
              }

              if (hasMore) {
                return loadMore;
              }

              if (!list.length) {
                return <Empty description={t('search.searchEmpty')} />;
              }

              return noMore;
            }}
          >
            {(loadEle) => {
              return (
                <>
                  {list.length > 0 && (
                    <div className={style.listBar}>
                      <div>{t('x_found', { x: list.length })}</div>
                    </div>
                  )}
                  <div ref={wrapperRef}>
                    {vList?.map((r) => (
                      <div key={r.index}>
                        <SearchPageItem type={type as Conversation.LocalSearchType} r={r.data} />
                      </div>
                    ))}
                  </div>
                  <div>
                    {loadEle}
                    {hasMore && !onlineSearch && offset ? (
                      <div style={{ textAlign: 'center' }}>
                        {new Date(parseInt(offset, 10)).toLocaleDateString()}
                      </div>
                    ) : null}
                  </div>
                </>
              );
            }}
          </LoadMore>
        </div>
      </SearchPageItemSelectionProvider>
    );

    return (
      <div className={style.specialTypeContainer}>
        {notFoundList.length > 0 && (
          <span>
            <div>
              {t('x_not_found', { x: notFoundList.length })}:{notFoundList.join(',')}
            </div>
            <Button
              type="link"
              onClick={() => {
                copyText(notFoundList.join(','))
                  .then(() => {
                    message.success(t('trade.复制成功'));
                  })
                  .catch(() => {
                    message.error(t('trade.复制失败'));
                  });
              }}
            >
              {t('aide.复制')}
            </Button>
          </span>
        )}
        {content}
      </div>
    );
  }
  return <Empty description={t('search.searchEmpty')} />;
}
